import type { AvatarControl, AvatarIncompatibility } from '@/api/model'
import { AvatarControlKindEnum } from '@/api/model'

export type AvatarWireConfig = Record<string, unknown>

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/

function stringValues(value: unknown): string[] {
  if (typeof value === 'string') return [value]
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string')
  }
  if (!isConfigObject(value)) return []
  return Object.entries(value)
    .filter(([, weight]) => isFiniteNumber(weight) && weight > 0)
    .map(([variant]) => variant)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function weightedVariants(raw: unknown, offered: ReadonlySet<string>) {
  if (!isConfigObject(raw)) return undefined
  const weighted = Object.fromEntries(
    Object.entries(raw).filter(
      ([variant, weight]) => offered.has(variant) && isFiniteNumber(weight) && weight >= 0,
    ),
  )
  return Object.keys(weighted).length > 0 ? weighted : undefined
}

/**
 * Keep only values that the active backend catalogue offers.
 *
 * Avatar configs outlive artwork uploads, while the option names and variants do not.
 * This defensive boundary is shared by display, creator and PUT payloads so stale
 * values can never reach DiceBear or be written back after a style activation.
 */
export function configForCatalog(
  config: unknown,
  controls: readonly AvatarControl[],
  defaults: unknown = {},
): AvatarWireConfig {
  const source = isConfigObject(config) ? config : {}
  const fallback = isConfigObject(defaults) ? defaults : {}
  const result: AvatarWireConfig = {}

  for (const control of controls) {
    const raw = control.option in source ? source[control.option] : fallback[control.option]
    if (raw === undefined) continue

    if (control.kind === AvatarControlKindEnum.toggle) {
      if (isFiniteNumber(raw) && raw >= 0 && raw <= 100) result[control.option] = raw
      continue
    }

    if (control.kind === AvatarControlKindEnum.color) {
      const values = (
        typeof raw === 'string' || Array.isArray(raw) ? stringValues(raw) : []
      ).filter((value) => HEX_COLOUR.test(value))
      if (values.length > 0) result[control.option] = values.slice(0, 1)
      continue
    }

    const offered = new Set((control.variants ?? []).map((variant) => variant.value))
    const weighted = weightedVariants(raw, offered)
    if (weighted) {
      result[control.option] = weighted
      continue
    }
    const values = stringValues(raw).filter((value) => offered.has(value))
    if (values.length > 0) result[control.option] = values.slice(0, 1)
  }

  return result
}

function includesValue(config: AvatarWireConfig, option: string, values: readonly string[]) {
  return stringValues(config[option]).some((value) => values.includes(value))
}

/** True when a candidate survives the catalogue's cross-option constraints. */
export function choiceAllowed(
  draft: AvatarWireConfig,
  option: string,
  value: unknown,
  incompatibilities: readonly AvatarIncompatibility[],
): boolean {
  const candidate = { ...draft, [option]: value }

  return !incompatibilities.some((rule) => {
    const conditionMatches = Object.entries(rule.when).every(([key, values]) =>
      includesValue(candidate, key, values),
    )
    if (!conditionMatches) return false
    return Object.entries(rule.disallow).some(([key, values]) =>
      includesValue(candidate, key, values),
    )
  })
}

export function isConfigObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Stable per identity across every view; never a product-wide shared constant. */
export function avatarSeed(identity: string | number) {
  return `user-${String(identity).trim().toLowerCase()}`
}

/** Whether an open editor's publication snapshot no longer matches live documents. */
export function avatarPublicationChanged(
  snapshotHash: string | null,
  styleHash: string | undefined,
  configHash: string | undefined,
) {
  if (!snapshotHash || !styleHash || !configHash) return false
  return snapshotHash !== styleHash || configHash !== styleHash
}
